import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState, useRef, useEffect, FormEvent } from 'react'
import { toast } from 'sonner'
import {
  MessageSquare,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Globe,
  Monitor,
  Eye,
  Activity,
  MessageCircle,
  CheckCircle2,
  PauseCircle,
  ExternalLink,
  User as UserIcon,
  Pencil,
  Calendar,
  ToggleLeft,
  List,
  StickyNote,
  Merge,
  Plus,
} from 'lucide-react'
import MessageAttachments from '@/components/MessageAttachments'
import MessageBody from '@/components/MessageBody'
import ImageLightbox from '@/components/ImageLightbox'
import { useLightbox } from '@/hooks/useLightbox'
import NavigationRail from '@/components/NavigationRail'
import {
  formatRelativeTime,
  formatTime,
  getInitials,
  getAvatarColor,
  formatEventType,
} from '@/lib/displayUtils'
import { stripHtml } from '@/lib/sanitizeHtml'
import MergeContactModal from '@/components/MergeContactModal'
import AdminActionsPanel from '@/components/inbox/AdminActionsPanel'
import type { UserActions, CompanyActions } from '@/components/inbox/types'
import type { Agent, Tag, Attachment } from '@/types/shared'

interface EndUserDetail {
  id: number
  name: string | null
  displayName: string
  email: string | null
  phone: string | null
  userId: string | null
  companyId: number | null
  companyName: string | null
  companyDomain: string | null
  locale: string
  city: string | null
  country: string | null
  location: string | null
  metadata: Record<string, unknown> | null
  firstSeenAt: string | null
  lastSeenAt: string | null
  signedUpAt: string | null
  webSessionsCount: number
  browserVersion: string | null
  os: string | null
  browserLanguage: string | null
  lastContactedAt: string | null
  lastHeardFromAt: string | null
  messagesCount: number
  conversationsCount: number
  eventsCount: number
  tags: Tag[]
  anonymous: boolean
  createdAt: string
  contactNotes: ContactNote[]
  userActions?: UserActions
  companyActions?: CompanyActions
}

interface ConversationItem {
  id: number
  status: string
  priority: string
  lastMessage: string | null
  lastMessageAt: string | null
  messagesCount: number
  assignedAgent: Agent | null
  tags: Tag[]
  createdAt: string
}

interface ContactNote {
  id: number
  body: string
  createdAt: string
  agent: Agent | null
}

interface MessageAuthor {
  id: number
  name: string
  email: string | null
}

interface Message {
  id: number
  body: string
  authorType: 'Agent' | 'EndUser'
  author: MessageAuthor
  createdAt: string
  readAt: string | null
  deliveredAt: string | null
  attachments: Attachment[]
}

interface Event {
  id: number
  eventType: string
  data: Record<string, unknown> | null
  createdAt: string
}

interface PageView {
  id: number
  url: string
  title: string | null
  referrer: string | null
  createdAt: string
}

interface DataAttributeDef {
  id: number
  name: string
  label: string | null
  description: string | null
  dataType: string
  model: string
  listOptions: string[]
}

interface Props {
  endUser: EndUserDetail
  conversations: ConversationItem[]
  selectedConversationId: number | null
  messages: Message[]
  currentAgent: Agent
  events: Event[]
  pageViews: PageView[]
  dataAttributes: DataAttributeDef[]
}

type TabType = 'conversations' | 'activity' | 'pages'

export default function UserDetail({
  endUser,
  conversations,
  selectedConversationId,
  messages,
  currentAgent,
  events,
  pageViews,
  dataAttributes,
}: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('conversations')
  const [showMessages, setShowMessages] = useState(selectedConversationId !== null)
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(selectedConversationId)
  const lightbox = useLightbox()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const editInputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const [editingField, setEditingField] = useState<'email' | 'phone' | null>(null)
  const [editFieldValue, setEditFieldValue] = useState('')
  const editFieldRef = useRef<HTMLInputElement>(null)
  const [localEmail, setLocalEmail] = useState(endUser.email)
  const [localPhone, setLocalPhone] = useState(endUser.phone)

  const contactNotes = endUser.contactNotes || []
  const {
    data: noteData,
    setData: setNoteData,
    post: postNote,
    processing: noteProcessing,
    reset: resetNote,
    errors: noteErrors,
  } = useForm({ body: '' })

  useEffect(() => {
    if (editingKey && editInputRef.current) {
      editInputRef.current.focus()
    }
  }, [editingKey])

  useEffect(() => {
    if (editingField && editFieldRef.current) {
      editFieldRef.current.focus()
    }
  }, [editingField])

  const startFieldEdit = (field: 'email' | 'phone') => {
    const currentValue = field === 'email' ? localEmail : localPhone
    setEditingField(field)
    setEditFieldValue(currentValue || '')
  }

  const saveFieldEdit = async () => {
    if (!editingField) return
    const field = editingField
    const newValue = editFieldValue
    setEditingField(null)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const response = await fetch(`/contacts/${endUser.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ end_user: { [field]: newValue } }),
    })

    if (response.ok) {
      // Update local state to reflect the change without a server round-trip
      if (field === 'email') setLocalEmail(newValue || null)
      else setLocalPhone(newValue || null)

      const label = field === 'email' ? 'E-mail' : 'Telefone'
      toast.success(`${label} atualizado com sucesso`)
    } else {
      toast.error('Erro ao atualizar campo')
    }
  }

  const cancelFieldEdit = () => setEditingField(null)

  const handleNoteSubmit = (event: FormEvent) => {
    event.preventDefault()
    postNote(`/contacts/${endUser.id}/notes`, {
      preserveScroll: true,
      onSuccess: () => resetNote(),
    })
  }

  const handleConversationSelect = (conversationId: number) => {
    setCurrentConversationId(conversationId)
    setShowMessages(true)
    router.visit(`/contacts/${endUser.id}?conversation_id=${conversationId}`, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleBackToList = () => {
    setShowMessages(false)
    setCurrentConversationId(null)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Aberta
          </span>
        )
      case 'closed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <CheckCircle2 className="w-3 h-3" />
            Fechada
          </span>
        )
      case 'snoozed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <PauseCircle className="w-3 h-3" />
            Adiada
          </span>
        )
      default:
        return null
    }
  }

  return (
    <>
      <Head title={`${endUser.displayName} - Detalhe do Contato`} />

      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="contacts" />

        {/* Contact Info Sidebar */}
        <aside className="w-[300px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          {/* Header with avatar and name */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Link
                href="/contacts/users"
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar aos contatos
              </Link>
              <button
                onClick={() => setMergeModalOpen(true)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
              >
                <Merge className="w-3.5 h-3.5" />
                Mesclar
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-medium ${getAvatarColor(
                  endUser.displayName
                )}`}
              >
                {getInitials(endUser.displayName)}
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{endUser.displayName}</h2>
                {endUser.companyName && (
                  endUser.companyId ? (
                    <Link
                      href={`/contacts/companies/${endUser.companyId}`}
                      className="text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 min-w-0"
                    >
                      <span className="truncate">{endUser.companyName}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </Link>
                  ) : (
                    <p className="text-sm text-gray-500">{endUser.companyName}</p>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <section className="p-4 border-b border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Contato
            </h3>
            <div className="space-y-2">
              {/* Email - editable */}
              <div className="group flex items-center gap-2 text-sm py-0.5 rounded hover:bg-gray-50 px-1 -mx-1">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                {editingField === 'email' ? (
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <input
                      ref={editFieldRef}
                      type="email"
                      value={editFieldValue}
                      onChange={(e) => setEditFieldValue(e.target.value)}
                      onBlur={saveFieldEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveFieldEdit()
                        if (e.key === 'Escape') cancelFieldEdit()
                      }}
                      placeholder="email@exemplo.com"
                      className="px-1.5 py-0.5 border border-blue-300 rounded text-xs flex-1 min-w-0 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                ) : localEmail ? (
                  <>
                    <span className="text-gray-600 truncate flex-1 min-w-0">{localEmail}</span>
                    <button
                      onClick={() => startFieldEdit('email')}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity cursor-pointer"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => startFieldEdit('email')}
                    className="text-gray-400 hover:text-gray-600 text-xs flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3 h-3" />
                    Adicionar e-mail
                  </button>
                )}
              </div>

              {/* Phone - editable */}
              <div className="group flex items-center gap-2 text-sm py-0.5 rounded hover:bg-gray-50 px-1 -mx-1">
                <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                {editingField === 'phone' ? (
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <input
                      ref={editFieldRef}
                      type="tel"
                      value={editFieldValue}
                      onChange={(e) => setEditFieldValue(e.target.value)}
                      onBlur={saveFieldEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveFieldEdit()
                        if (e.key === 'Escape') cancelFieldEdit()
                      }}
                      placeholder="+55 (11) 99999-9999"
                      className="px-1.5 py-0.5 border border-blue-300 rounded text-xs flex-1 min-w-0 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                ) : localPhone ? (
                  <>
                    <span className="text-gray-600 flex-1 min-w-0">{localPhone}</span>
                    <button
                      onClick={() => startFieldEdit('phone')}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity cursor-pointer"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => startFieldEdit('phone')}
                    className="text-gray-400 hover:text-gray-600 text-xs flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3 h-3" />
                    Adicionar telefone
                  </button>
                )}
              </div>

              {endUser.location && (
                <div className="flex items-center gap-2 text-sm py-0.5 px-1 -mx-1">
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-600">{endUser.location}</span>
                </div>
              )}
              {endUser.userId && (
                <div className="flex items-center gap-2 text-sm py-0.5 px-1 -mx-1">
                  <UserIcon className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-600 truncate">{endUser.userId}</span>
                </div>
              )}
            </div>
          </section>

          {/* Activity Stats */}
          <section className="p-4 border-b border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Atividade
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span className="text-xs">Conversas</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{endUser.conversationsCount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="text-xs">Mensagens</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{endUser.messagesCount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <Eye className="w-3.5 h-3.5" />
                  <span className="text-xs">Sessões</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{endUser.webSessionsCount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <Activity className="w-3.5 h-3.5" />
                  <span className="text-xs">Eventos</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{endUser.eventsCount}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Primeira visita</span>
                <span className="text-gray-700">{formatRelativeTime(endUser.firstSeenAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Última visita</span>
                <span className="text-gray-700">{formatRelativeTime(endUser.lastSeenAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Último contato</span>
                <span className="text-gray-700">{formatRelativeTime(endUser.lastContactedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Última resposta</span>
                <span className="text-gray-700">{formatRelativeTime(endUser.lastHeardFromAt)}</span>
              </div>
            </div>
          </section>

          {/* System Info */}
          {(endUser.os || endUser.browserVersion) && (
            <section className="p-4 border-b border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Sistema
              </h3>
              <div className="space-y-2">
                {endUser.os && (
                  <div className="flex items-center gap-2 text-sm">
                    <Monitor className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-gray-600">{endUser.os}</span>
                  </div>
                )}
                {endUser.browserVersion && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-gray-600">{endUser.browserVersion}</span>
                  </div>
                )}
                {endUser.browserLanguage && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-4 h-4 text-gray-400 shrink-0 text-center text-xs">🌐</span>
                    <span className="text-gray-600">{endUser.browserLanguage}</span>
                  </div>
                )}
                {endUser.locale && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-4 h-4 text-gray-400 shrink-0 text-center text-xs">📍</span>
                    <span className="text-gray-600">{endUser.locale}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Tags */}
          {endUser.tags.length > 0 && (
            <section className="p-4 border-b border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Tags
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {endUser.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${tag.color}15`,
                      color: tag.color,
                    }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Admin Actions */}
          <AdminActionsPanel
            userActions={endUser.userActions}
            companyActions={endUser.companyActions}
          />

          {/* Contact Notes */}
          <section className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5" />
                Notas
              </h3>
              <span className="text-xs text-gray-400">{contactNotes.length}</span>
            </div>
            <form onSubmit={handleNoteSubmit} className="space-y-2">
              <textarea
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Adicionar nota interna..."
                value={noteData.body}
                onChange={(e) => setNoteData('body', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    if (noteData.body.trim()) e.currentTarget.form?.requestSubmit()
                  }
                }}
              />
              {noteErrors.body && (
                <p className="text-xs text-red-600">{noteErrors.body}</p>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={noteProcessing || !noteData.body.trim()}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Salvar nota
                </button>
              </div>
            </form>
            <div className="mt-3 space-y-2">
              {contactNotes.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma nota ainda</p>
              ) : (
                contactNotes.map((note) => (
                  <div key={note.id} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.body}</p>
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                      <span>{note.agent?.name || 'Sistema'}</span>
                      <span>{formatRelativeTime(note.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Metadata / Custom Attributes */}
          {endUser.metadata && Object.keys(endUser.metadata).length > 0 && (
            <section className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dados Personalizados
              </h3>
              <div className="space-y-1.5">
                {Object.entries(endUser.metadata).map(([key, value]) => {
                  const attrDef = dataAttributes?.find(da => da.name === key)
                  const displayLabel = attrDef?.label || key
                  const dataType = attrDef?.dataType || 'string'

                  const formatValue = (val: unknown): string => {
                    if (val === null || val === undefined) return '—'
                    switch (dataType) {
                      case 'boolean': return val ? 'Sim' : 'Não'
                      case 'date':
                      case 'datetime': return val ? formatRelativeTime(String(val)) : '—'
                      case 'integer': return Number(val).toLocaleString('pt-BR')
                      case 'float': return Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                      default: return String(val)
                    }
                  }

                  const startEditing = () => {
                    setEditingKey(key)
                    if (dataType === 'boolean') {
                      setEditValue(value ? 'true' : 'false')
                    } else {
                      setEditValue(value !== null && value !== undefined ? String(value) : '')
                    }
                  }

                  const saveEdit = () => {
                    router.patch(`/contacts/${endUser.id}/update_metadata`, {
                      key, value: editValue, data_type: dataType,
                    }, { preserveScroll: true, preserveState: true })
                    setEditingKey(null)
                  }

                  const cancelEdit = () => setEditingKey(null)

                  const TypeIndicator = () => {
                    switch (dataType) {
                      case 'boolean': return <ToggleLeft className="w-3 h-3 text-gray-400" />
                      case 'date':
                      case 'datetime': return <Calendar className="w-3 h-3 text-gray-400" />
                      case 'list': return <List className="w-3 h-3 text-gray-400" />
                      case 'integer':
                      case 'float': return <span className="text-[10px] font-mono text-gray-400">#</span>
                      default: return null
                    }
                  }

                  return (
                    <div key={key} className="group flex items-start justify-between text-sm py-1 rounded hover:bg-gray-50 px-1 -mx-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <TypeIndicator />
                        <span className="text-gray-500 truncate" title={key}>{displayLabel}</span>
                      </div>
                      {editingKey === key ? (
                        <div className="flex items-center gap-1">
                          {dataType === 'boolean' ? (
                            <select
                              ref={editInputRef as React.RefObject<HTMLSelectElement>}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                            >
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                            </select>
                          ) : dataType === 'list' && attrDef?.listOptions?.length ? (
                            <select
                              ref={editInputRef as React.RefObject<HTMLSelectElement>}
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value) }}
                              onBlur={saveEdit}
                              onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none bg-white max-w-[140px]"
                            >
                              <option value="">—</option>
                              {attrDef.listOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              ref={editInputRef as React.RefObject<HTMLInputElement>}
                              type={dataType === 'integer' || dataType === 'float' ? 'number' : dataType === 'date' || dataType === 'datetime' ? 'date' : 'text'}
                              step={dataType === 'float' ? '0.01' : dataType === 'integer' ? '1' : undefined}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit()
                                if (e.key === 'Escape') cancelEdit()
                              }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs w-[120px] focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700 truncate max-w-[140px]" title={String(value)}>
                            {formatValue(value)}
                          </span>
                          <button
                            onClick={startEditing}
                            className="p-0.5 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {showMessages && currentConversationId ? (
            /* Messages View */
            <>
              <header className="h-14 border-b border-gray-200 flex items-center px-4 gap-3">
                <button
                  onClick={handleBackToList}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                  <h2 className="text-sm font-semibold text-gray-900">Conversa</h2>
                  <p className="text-xs text-gray-500">{messages.length} mensagens</p>
                </div>
                <Link
                  href={`/inbox/${currentConversationId}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir na Caixa de Entrada
                </Link>
              </header>

              {/* Messages Timeline */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-2xl mx-auto space-y-3">
                  {messages.map((message, index) => {
                    const isAgent = message.authorType === 'Agent'
                    const prevMessage = messages[index - 1]
                    const showAvatar =
                      index === 0 || prevMessage.authorType !== message.authorType

                    return (
                      <div
                        key={message.id}
                        className={`flex gap-2 ${isAgent ? 'flex-row-reverse' : ''}`}
                      >
                        <div className="flex-shrink-0">
                          {showAvatar ? (
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(
                                message.author.name
                              )}`}
                            >
                              {getInitials(message.author.name)}
                            </div>
                          ) : (
                            <div className="w-7" />
                          )}
                        </div>
                        <div
                          className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'} max-w-[70%]`}
                        >
                          {showAvatar && (
                            <span className="text-xs text-gray-500 mb-0.5 px-1">
                              {message.author.name}
                            </span>
                          )}
                          <div
                            className={`px-3 py-2 rounded-2xl text-sm ${
                              isAgent
                                ? 'bg-blue-100 text-gray-900 rounded-br-md'
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }`}
                          >
                            {message.body && <MessageBody content={message.body} onImageClick={lightbox.open} />}
                            {message.attachments && message.attachments.length > 0 && (
                              <MessageAttachments
                                attachments={message.attachments}
                                onImageClick={(images, idx) => lightbox.open(images, idx)}
                              />
                            )}
                          </div>
                          <span className="text-xs text-gray-400 mt-0.5 px-1">
                            {formatTime(message.createdAt)}
                          </span>
                        </div>
                      </div>
                    )
                  })}

                  {messages.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Nenhuma mensagem nesta conversa</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Tabs View */
            <>
              {/* Tabs Header */}
              <div className="h-14 border-b border-gray-200 flex items-center px-4 gap-2">
                <button
                  onClick={() => setActiveTab('conversations')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    activeTab === 'conversations'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Conversas ({conversations.length})
                </button>
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    activeTab === 'activity'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Atividade ({events.length})
                </button>
                <button
                  onClick={() => setActiveTab('pages')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    activeTab === 'pages'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Páginas ({pageViews.length})
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'conversations' && (
                  <div className="divide-y divide-gray-100">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => handleConversationSelect(conv.id)}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                          currentConversationId === conv.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="relative shrink-0">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                                endUser.displayName
                              )}`}
                            >
                              {getInitials(endUser.displayName)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              {getStatusBadge(conv.status)}
                              {conv.assignedAgent && (
                                <span className="text-xs text-gray-400 truncate">
                                  • {conv.assignedAgent.name}
                                </span>
                              )}
                            </div>
                            {conv.lastMessage && (
                              <p className="text-xs text-gray-500 truncate block">
                                {stripHtml(conv.lastMessage)}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <span className="text-xs text-gray-400">
                              {formatRelativeTime(conv.lastMessageAt)}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}

                    {conversations.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Nenhuma conversa ainda</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'activity' && (
                  <div className="divide-y divide-gray-100">
                    {events.map((event) => (
                      <div key={event.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Activity className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {formatEventType(event.eventType)}
                            </p>
                            {event.data && Object.keys(event.data).length > 0 && (
                              <p className="text-xs text-gray-500 truncate mt-0.5">
                                {JSON.stringify(event.data)}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {formatRelativeTime(event.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {events.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Nenhuma atividade registrada</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'pages' && (
                  <div className="divide-y divide-gray-100">
                    {pageViews.map((pv) => (
                      <div key={pv.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start gap-3">
                          <span className="text-xs text-gray-400 w-14 shrink-0 pt-0.5">
                            {formatRelativeTime(pv.createdAt)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <a
                              href={pv.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline block truncate"
                            >
                              {pv.title || new URL(pv.url).pathname}
                            </a>
                            <p className="text-xs text-gray-400 truncate">{pv.url}</p>
                            {pv.referrer && (
                              <p className="text-xs text-gray-400 truncate mt-0.5">
                                De: {pv.referrer}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {pageViews.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Nenhuma página visualizada</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {mergeModalOpen && (
        <MergeContactModal
          primary={endUser}
          onClose={() => setMergeModalOpen(false)}
        />
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightbox.isOpen}
        images={lightbox.images}
        currentIndex={lightbox.currentIndex}
        onClose={lightbox.close}
        onNext={lightbox.next}
        onPrev={lightbox.prev}
      />
    </>
  )
}
